import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Alert, AlertDescription } from "../ui/alert"
import { Mail, ArrowLeft, CheckCircle, Brain, Eye, EyeOff, Lock } from "lucide-react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { apiService } from "../../lib/api"

// Brand colors from landing page and my-account
const BRAND_COLORS = {
  primary: '#c39d76', // Warm beige/tan
  secondary: '#d4a574', // Lighter tan
  dark: '#2C2416', // Dark brown
  darkGrey: '#3F454A', // Dark grey
  accent: '#dfc9b1', // Light beige
}

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

const resetPasswordSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>
type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export function ForgotPasswordForm() {
  // Check for token immediately on mount (before state initialization)
  const initialToken = typeof window !== 'undefined' 
    ? new URLSearchParams(window.location.search).get('token')
    : null
  
  const [step, setStep] = useState<'request' | 'reset'>(initialToken ? 'reset' : 'request')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [resetToken, setResetToken] = useState(initialToken || "")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const {
    register: registerForgot,
    handleSubmit: handleForgotSubmit,
    formState: { errors: forgotErrors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    formState: { errors: resetErrors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const [resetLink, setResetLink] = useState<string>("")

  const onForgotPasswordSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    setError("")
    setSuccess("")
    setResetLink("")
    
    try {
      const response = await apiService.forgotPassword(data.email) as any
      if (response.resetLink) {
        // Development mode - show the reset link
        setResetLink(response.resetLink)
        setSuccess("Password reset link generated. Click the link below to reset your password.")
      } else {
        setSuccess(response.message || "Password reset link has been sent to your email address.")
      }
    } catch (err: any) {
      console.error("❌ ForgotPasswordForm: Request failed:", err)
      // Check if it's a 404 (email not found) or contains email_not_found
      if (err.status === 404 || err.body?.error === 'email_not_found' || 
          err.message?.includes('email_not_found') || 
          err.message?.includes('No account found')) {
        setError("No account found with this email address. Please check your email and try again.")
      } else {
        setError(err.message || "Failed to send password reset link. Please try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onResetPasswordSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true)
    setError("")
    setSuccess("")
    
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token') || resetToken
      
      if (!token) {
        setError("Reset token is missing. Please use the link from your email.")
        setIsLoading(false)
        return
      }

      await apiService.resetPassword(token, data.password)
      setSuccess("Your password has been reset successfully. You can now login with your new password.")
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/auth/login")
      }, 2000)
    } catch (err: any) {
      console.error("❌ ForgotPasswordForm: Reset failed:", err)
      setError(err.message || "Failed to reset password. The link may have expired. Please request a new one.")
    } finally {
      setIsLoading(false)
    }
  }

  // Check if we have a token in URL (for reset step) - runs immediately and on URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const token = urlParams.get('token')
    console.log('🔍 ForgotPasswordForm: Checking for reset token in URL:', { 
      token: token ? `${token.substring(0, 10)}...` : null, 
      search: location.search, 
      pathname: location.pathname,
      fullUrl: window.location.href 
    })
    
    if (token && token.length > 0) {
      console.log('✅ ForgotPasswordForm: Token found, switching to reset step')
      setResetToken(token)
      setStep('reset')
      setError("") // Clear any previous errors
      setSuccess("") // Clear any previous success messages
    } else if (!token) {
      // Only reset to request step if there's definitely no token
      console.log('ℹ️ ForgotPasswordForm: No token found, showing request form')
      if (step !== 'request') {
        setStep('request')
      }
    }
  }, [location.search, location.pathname])

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Black Overlay */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(/log-sin-bg.jpg)",
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="text-center text-white space-y-6">
            <img 
              src="/brand-b.png" 
              alt="Brand Logo" 
              className="h-24 w-24 object-contain mx-auto mb-8"
            />
            <h1 className="text-4xl font-bold">Welcome to IKORO</h1>
            <p className="text-lg text-gray-200">Join us and start your journey with ikoro</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-white">
        <div className="w-full max-w-md">
        <div className="text-center space-y-2 pb-4">
          {/* Brand Logo - Compact */}
          <div className="mx-auto">
            <img 
              src="/brand-b.png" 
              alt="Brand Logo" 
              className="h-16 w-16 object-contain mx-auto"
            />
          </div>
          <h1 className="text-xl font-bold" style={{ color: BRAND_COLORS.dark }}>
            {step === 'request' ? 'Forgot Password' : 'Reset Password'}
          </h1>
          <p className="text-sm" style={{ color: BRAND_COLORS.darkGrey }}>
            {step === 'request' 
              ? "Enter your email address and we'll send you a link to reset your password"
              : 'Enter your new password'}
          </p>
        </div>
        
        <div className="space-y-3 px-6 pb-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {success && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {success}
                {resetLink && (
                  <div className="mt-3 p-3 bg-white rounded border" style={{ borderColor: `${BRAND_COLORS.primary}40` }}>
                    <p className="text-xs font-medium mb-2" style={{ color: BRAND_COLORS.dark }}>Development Mode - Reset Link:</p>
                    <a 
                      href={resetLink} 
                      className="text-xs hover:underline break-all font-medium"
                      style={{ color: BRAND_COLORS.primary }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {resetLink}
                    </a>
                    <p className="text-xs mt-2" style={{ color: BRAND_COLORS.darkGrey }}>Click the link above to reset your password</p>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {step === 'request' ? (
            <form 
              onSubmit={(e) => {
                e.preventDefault()
                handleForgotSubmit(onForgotPasswordSubmit)(e)
              }} 
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className={`pl-10 ${forgotErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    style={forgotErrors.email ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                    {...registerForgot("email")}
                    disabled={isLoading}
                  />
                </div>
                {forgotErrors.email && (
                  <p className="text-sm text-red-600 font-medium">{forgotErrors.email.message}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full font-semibold py-2.5"
                disabled={isLoading}
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                  color: 'white',
                }}
              >
                {isLoading ? "Sending..." : "Send Reset Link"}
              </Button>
              
              <div className="flex items-center justify-center pt-1">
                <Link
                  to="/auth/login"
                  className="text-xs hover:underline font-medium flex items-center gap-1"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to login
                </Link>
              </div>
            </form>
          ) : (
            <form 
              onSubmit={(e) => {
                e.preventDefault()
                handleResetSubmit(onResetPasswordSubmit)(e)
              }} 
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your new password"
                    className={`pl-10 pr-10 ${resetErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    style={resetErrors.password ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                    {...registerReset("password")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70"
                    style={{ color: BRAND_COLORS.primary }}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {resetErrors.password && (
                  <p className="text-sm text-red-600 font-medium">{resetErrors.password.message}</p>
                )}
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword" className="text-sm">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your new password"
                    className={`pl-10 pr-10 ${resetErrors.confirmPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    style={resetErrors.confirmPassword ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                    {...registerReset("confirmPassword")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70"
                    style={{ color: BRAND_COLORS.primary }}
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {resetErrors.confirmPassword && (
                  <p className="text-sm text-red-600 font-medium">{resetErrors.confirmPassword.message}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full font-semibold py-2.5"
                disabled={isLoading}
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                  color: 'white',
                }}
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
              
              <div className="flex items-center justify-center pt-1">
                <Link
                  to="/auth/login"
                  className="text-xs hover:underline font-medium flex items-center gap-1"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
      </div>
    </div>
  )
}

